import {
    ChangeDetectionStrategy,
    Component,
    ElementRef,
    EventEmitter,
    NgZone,
    OnDestroy,
    OnInit,
    Output,
    ViewChild,
    ViewEncapsulation,
} from '@angular/core';
import { AppStateFacade } from '@app-state';
import {
    ISidebarSection,
    PageMetadata,
    PageMetadataRegistry,
    SIDEBAR_SECTION_TYPES,
    SidebarSectionType,
} from '@app/router';
import { IFolder } from '@models';
import {
    AnalyticsService,
    StorageService,
} from '@services';

import {
    combineLatest,
    merge,
    Observable,
    of,
    Subject,
} from 'rxjs';
import {
    distinctUntilChanged,
    map,
    startWith,
    switchMap,
    take,
    takeUntil,
} from 'rxjs/operators';

@Component({
    selector: 'ui-sidebar-navigation',
    templateUrl: './ui-sidebar-navigation.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
    styleUrls: [
        './ui-sidebar-navigation.component.scss',
    ],
    encapsulation: ViewEncapsulation.None,
    providers: [StorageService],
})
export class UiSidebarNavigationComponent implements OnInit, OnDestroy {
    public expansionStates!: Record<SidebarSectionType, boolean>;

    @Output()
    public widthChange = new EventEmitter<number>();

    @ViewChild('navList', { static: false })
    public navList?: ElementRef<HTMLElement>;

    public isHost!: boolean;

    public currentFolder!: IFolder;

    public sidebarSections$!: Observable<ISidebarSection[]>;

    private _destroyed$ = new Subject();
    private _storageKeyPrefix = `sidebarState_u${this._appState.user.value!.id}_`;

    constructor(
        private _appState: AppStateFacade,
        private _pageRegistry: PageMetadataRegistry,
        private _storage: StorageService,
        private _analytics: AnalyticsService,
        private _zone: NgZone,
    ) { }

    ngOnInit() {
        this.isHost = this._appState.user.loggedInAsHost;

        // set the sections to expanded if not otherwise defined in localStorage
        this.expansionStates = SIDEBAR_SECTION_TYPES.reduce((acc, section) => {
            acc[section as SidebarSectionType] = this._storage.get(`${this._storageKeyPrefix}${section}`, true);
            return acc;
        }, {} as Record<SidebarSectionType, boolean>);

        this.sidebarSections$ = this._appState.user.folder.value$
            .pipe(
                switchMap(() => of(this._pageRegistry.sidebarSections)),
            );

        this._appState.user.folder.value$
            .pipe(
                takeUntil(this._destroyed$),
            ).subscribe(folder => {
                this.currentFolder = folder!;
            });

        const changeWidthFactors$ = merge(
            this._appState.user.folder.change$,
            this._appState.language.change$,
        );

        changeWidthFactors$
            .pipe(
                startWith(''),
                switchMap(() => this._zone.onStable.pipe(take(1))),
                map(_ => this.navList!.nativeElement.clientWidth),
                distinctUntilChanged(),
                takeUntil(this._destroyed$),
            ).subscribe(width => {
                this.widthChange.next(width);
            });
    }

    ngOnDestroy() {
        this._destroyed$.next();
    }

    public containsActive = (pages: PageMetadata[]) =>
        combineLatest(
            pages.map(page => this.isActive(page)),
        ).pipe(
            map(activeFlags => activeFlags.some(Boolean)),
        )

    public isActive = (page: PageMetadata) =>
        this._pageRegistry.hasActivePage(page)

    public onExpandedChange(section: SidebarSectionType, state: boolean) {
        const oldState = this._storage.get(`${this._storageKeyPrefix}${section}`, true);
        if (oldState === state) { return; }

        this._storage.set(`${this._storageKeyPrefix}${section}`, state);
        this._analytics.track(`Action: ${state ? 'Expand' : 'Collapse'} Sidebar ${section} Section`);
    }
}
